import { useCallback, useEffect, useState } from "react";
import { fetchCameraSnapshotBlobCached } from "../../api/cameras.ts";

const SNAPSHOT_PLACEHOLDER_SRC = `${process.env.PUBLIC_URL ?? ""}/images/camera-snapshot-placeholder.svg`;

export type CameraGridSnapshotProps = {
  cameraId: string;
  cam360Rtsp: string | null | undefined;
  version: string;
};

export function CameraGridSnapshot({
  cameraId,
  cam360Rtsp,
  version,
}: CameraGridSnapshotProps) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [phase, setPhase] = useState<"empty" | "loading" | "ready" | "error">(
    "empty",
  );

  const onSnapshotImgError = useCallback(() => {
    setObjectUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setPhase("error");
  }, []);

  useEffect(() => {
    const trimmed = cam360Rtsp?.trim() ?? "";
    if (!trimmed) {
      setObjectUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setPhase("empty");
      return;
    }

    let cancelled = false;
    let created: string | null = null;
    setPhase("loading");

    void (async () => {
      try {
        const blob = await fetchCameraSnapshotBlobCached(cameraId, version);
        if (cancelled) return;
        const u = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(u);
          return;
        }
        created = u;
        setObjectUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return u;
        });
        setPhase("ready");
      } catch {
        if (cancelled) return;
        setObjectUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
        setPhase("error");
      }
    })();

    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
      setObjectUrl((prev) => {
        if (prev && prev !== created) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [cameraId, cam360Rtsp, version]);

  if (phase === "empty") {
    return <span className="ds-cam-card__shot-placeholder">No 360° stream</span>;
  }
  if (phase === "loading") {
    return (
      <span className="ds-cam-card__shot-placeholder">Loading snapshot…</span>
    );
  }
  if (phase === "error") {
    return (
      <img
        src={SNAPSHOT_PLACEHOLDER_SRC}
        alt=""
        className="ds-cam-card__shot-img"
        decoding="async"
      />
    );
  }

  if (phase === "ready" && objectUrl) {
    return (
      <img
        src={objectUrl}
        alt=""
        className="ds-cam-card__shot-img"
        decoding="async"
        onError={onSnapshotImgError}
      />
    );
  }

  return (
    <img
      src={SNAPSHOT_PLACEHOLDER_SRC}
      alt=""
      className="ds-cam-card__shot-img"
      decoding="async"
    />
  );
}
